import { createAsyncThunk } from '@reduxjs/toolkit'
import { getNotificationMessage } from 'store'
import {
  setMeXmppInitialState,
  changeNumUnread,
  clearXmppPushNotification,
  messageReceived,
  syncStateChanged,
  setOnlineMatrixList,
  setMatrixAccNameChange
} from 'store/chat/slice'
import { usersApi } from 'store/users/api'
import {
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import * as sdk from 'matrix-js-sdk'
import {
  // getParsedPassword,
  getParsedPassword2
} from 'utils/helpers'
import { decodeRecoveryKey } from 'matrix-js-sdk/lib/crypto-api'
import InitChat from 'components/Messenger/chatui/chat_init'
import { getMatrixClient, setMatrixClient } from 'components/Messenger/Matrix/clientStore'

const isLocalEnv = (process.env.REACT_APP_USE_LOCAL_XMP !== undefined && process.env.REACT_APP_USE_LOCAL_XMP === 'true')

let refreshTimerId = null

const secretStorageKeys = new Map()

const switchTestUser = () => {
  let res = ''
  switch (window.testFlip) {
  case 'test01@localhost':
    res = 'test04@localhost'
    break
  case 'test04@localhost':
    res = 'test01@localhost'
    break
  default:
    res = 'test04@localhost'
  }
  window.testFlip = res
  return res
}

export const getChatCredentials = createAsyncThunk(
  'chat/getChatCredentials',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const response = await usersApi.getChatCredentials(body)

      return response.data
    } catch (error) {
      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Get Chat Credentials Error'
        })
      )
      return rejectWithValue(error.message)
    }
  }
)

const doConnectChat = async (chat) => {
  // if invalid data then exit
  if (chat.chatCredentials === null || !chat.chatCredentials.username
    || !chat.chatCredentials.password) {
    return Promise.resolve()
  }
  // const password = getParsedPassword(chat.chatCredentials.password)
  const password2 = await getParsedPassword2(chat.chatCredentials.password)
  const discover_connection_methods = false
  const ws_url = isLocalEnv ? 'ws://localhost:5280/ws/' : `wss://${process.env.REACT_APP_XMPP_CHAT_URL}:5443/ws`
  const jid = isLocalEnv ? switchTestUser() : `${chat.chatCredentials.username}@${process.env.REACT_APP_XMPP_CHAT_URL}`
  const pass = isLocalEnv ? 'pass' : password2
  if (isLocalEnv) {
    console.log(`connecting to chat with jid: "${jid}"`)
  }
  return InitChat(jid, pass, discover_connection_methods, undefined, ws_url)
}

export const LogoutFromChat = createAsyncThunk(
  'chat/LogoutFromChat',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const { chat } = getState()
      await dispatch(changeNumUnread(0))
      await dispatch(clearXmppPushNotification())
      if (!chat.meXmppIsOnline) {
        return dispatch(setMeXmppInitialState())
      }
      const notifyUiAfterLogoutDone = () => new Promise((resolve) => {
        window.addEventListener('custom-chat-logout-complete', resolve, { once: true })
      })

      const event = new CustomEvent('custom-chat-logout')
      window.dispatchEvent(event)
      await notifyUiAfterLogoutDone()
      return dispatch(setMeXmppInitialState())
    } catch (error) {
      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Login chat user error'
        })
      )
      return rejectWithValue(error.message)
    }
  }
)

export const ConnectToChat = createAsyncThunk(
  'chat/ConnectToChat',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const { chat } = getState()
      if (chat.meXmppIsOnline) {
        await dispatch(LogoutFromChat())
      }

      await dispatch(setMeXmppInitialState())
      return doConnectChat(chat)
    } catch (error) {
      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Login chat user error'
        })
      )
      return rejectWithValue(error.message)
    }
  }
)

// Matrix client functions

export const refreshMatrixToken = createAsyncThunk(
  'matrix/refreshToken',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      if (!client) throw new Error('No active Matrix client')

      const currentRefreshToken = client.getRefreshToken?.()
      if (!currentRefreshToken) throw new Error('No refresh token available')

      const response = await client.refreshToken(currentRefreshToken)

      // client.http.opts.accessToken = response.access_token
      client.setAccessToken(response.access_token)

      // Re-schedule the next refresh using the new expiry
      dispatch(
        // eslint-disable-next-line no-use-before-define
        scheduleTokenRefresh({
          expiresInMs: response.expires_in_ms
        })
      )

      return {
        accessToken: response.access_token,
        refreshToken: response.refresh_token
      }
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

export const scheduleTokenRefresh = createAsyncThunk(
  'matrix/scheduleTokenRefresh',
  async ({ expiresInMs }, { dispatch }) => {
    if (refreshTimerId) clearTimeout(refreshTimerId)

    const refreshInMs = Math.max(expiresInMs - 30000, 5000)

    refreshTimerId = setTimeout(() => {
      dispatch(refreshMatrixToken())
    }, refreshInMs)

    return true
  }
)

export const clearScheduledTokenRefresh = createAsyncThunk(
  'matrix/clearScheduledTokenRefresh',
  async () => {
    if (refreshTimerId) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }
    return true
  }
)

const waitForRoom = (client, roomId, timeoutMs = 20000) => new Promise((resolve) => {
  let done = false
  let checking = false
  let timer = null
  let check = null

  const finish = (value) => {
    if (done) return
    done = true
    clearTimeout(timer)
    client.off(sdk.ClientEvent.Room, check)
    client.off(sdk.RoomStateEvent.Members, check)
    resolve(value)
  }

  check = async () => {
    if (done || checking) return
    checking = true
    try {
      const room = client.getRoom(roomId)
      if (room && room.getMyMembership() === 'join') {
        await room.loadMembersIfNeeded()
        if (room.getJoinedMembers().length >= 2) finish(room)
      }
    } finally {
      checking = false
    }
  }

  timer = setTimeout(() => finish(client.getRoom(roomId) || null), timeoutMs)

  client.on(sdk.ClientEvent.Room, check)
  client.on(sdk.RoomStateEvent.Members, check)
  check()
})

export const getRoomInfo = createAsyncThunk(
  'chat/getRoomInfo',
  async (body, { rejectWithValue, getState }) => {
    try {
      const { chat } = getState()
      const client = getMatrixClient()

      let roomWithInfo = []
      let found = false

      if (body?.type === 'direct') {
        found = chat.directRoomList.find((dr) => dr?.roomId === body?.roomId)
      }

      if (found) {
        roomWithInfo = found
      } else {
        const room = await waitForRoom(client, body?.roomId)
        if (!room) return rejectWithValue('Room not available yet')

        room.recalculate()

        roomWithInfo = {
          roomId: body?.roomId,
          name: room?.name,
          type: body?.type
        }
      }

      return roomWithInfo
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const getDirectRooms = createAsyncThunk(
  'chat/getDirectRooms',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await usersApi.getDirectRooms()

      const { chat } = getState()
      const client = getMatrixClient()

      let roomWithInfo = []
      if (response.data.data.length) {
        roomWithInfo = response.data.data.map((d) => {
          const found = chat.directRoomList.find((dr) => dr?.roomId === d)

          if (found) {
            d = found
          } else {
            const roomInfo = client.getRoom(d)
            d = {
              roomId: d,
              name: roomInfo?.name
            }
          }

          return d
        })
      }

      return roomWithInfo
    } catch (error) {
      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Get direct rooms error'
        })
      )

      return rejectWithValue(error.message)
    }
  }
)

export const fetchAvatarData = createAsyncThunk(
  'matrix/fetchAvatarData',
  async (body, { rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      const res = await fetch(body.httpUrl, {
        headers: { Authorization: `Bearer ${client.getAccessToken()}` }
      })

      if (!res.ok) return null

      const blob = await res.blob()
      return URL.createObjectURL(blob)
    } catch (err) {
      console.error(err)
      return rejectWithValue(err.message)
    }
  }
)

//  start client
export const startMatrixClient = createAsyncThunk(
  'matrix/startClient',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      const client = sdk.createClient({
        baseUrl: credentials.baseUrl,
        accessToken: credentials.accessToken,
        userId: credentials.userId,
        deviceId: credentials.deviceId,
        refreshToken: credentials.refreshToken,
        tokenRefreshFunction: async (refreshToken) => {
          const response = await client.refreshToken(refreshToken)

          return {
            accessToken: response.access_token,
            refreshToken: response.refresh_token
          }
        },
        cryptoCallbacks: {
          // Called when the SDK creates or unlocks a secret storage key
          cacheSecretStorageKey: (keyId, keyInfo, privateKey) => {
            secretStorageKeys.set(keyId, privateKey)
          },

          // Called whenever the SDK needs to read/write secret storage
          getSecretStorageKey: async ({ keys }, secretName) => {
            for (const keyId of Object.keys(keys)) {
              const key = secretStorageKeys.get(keyId)
              if (key) {
                return [keyId, key]
              }
            }

            // Not cached (e.g. after a page reload): prompt the user for
            // their recovery key here, decode it, and return [keyId, key].
            return null
          }
        }
      })

      setMatrixClient(client)

      try {
        await client.initRustCrypto({
          cryptoDatabasePrefix: `connector-${credentials.userId}-${credentials.deviceId}`
        })
      } catch (error) {
        console.error('Matrix crypto initialization failed:', error)
        throw error
      }

      // Handle incoming timeline events.
      client.on('Room.timeline', (event, room) => {
        if (
          event.getType() === 'm.room.message'
          && !event.isRedacted()
        ) {
          dispatch(
            messageReceived({
              roomId: room.roomId,
              sender: event.getSender(),
              body: event.getContent().body,
              ts: event.getTs()
            })
          )
        }
      })

      client.on('Event.decrypted', (event) => {
        if (
          event.getType() === 'm.room.message'
          && !event.isRedacted()
        ) {
          dispatch(
            messageReceived({
              roomId: event.getRoomId(),
              sender: event.getSender(),
              body: event.getContent().body,
              ts: event.getTs()
            })
          )
        }
      })

      client.on('sync', (state) => {
        dispatch(syncStateChanged(state))
      })

      // Start syncing.
      const prepared = new Promise((resolve, reject) => {
        const onSync = (state, previousState, error) => {
          const cleanup = () => {
            client.removeListener('sync', onSync)
          }

          if (state === 'PREPARED') {
            cleanup()
            resolve()
          } else if (state === 'ERROR') {
            cleanup()
            reject(error || new Error('Matrix sync failed'))
          }
        }

        client.on('sync', onSync)
      })

      client.on(sdk.UserEvent.Presence, (event, user) => {
        const c = event.getContent()
        const isOnline = c.presence === 'online'
          && (c.currently_active || (c.last_active_ago ?? Infinity) < 60_000)

        dispatch(setOnlineMatrixList({
          userId: event.getSender() ?? user.userId,
          presence: isOnline ? 'online' : 'offline'
        }))
      })

      await client.startClient({ initialSyncLimit: 20 })
      await prepared

      if (credentials.expiresInMs) {
        dispatch(
          scheduleTokenRefresh({
            expiresInMs: credentials.expiresInMs
          })
        )
      }

      client.on(sdk.RoomStateEvent.Members, (event) => {
        const userId = event.getStateKey()
        if (userId === client.getUserId()) return

        const roomId = event.getRoomId()
        const content = event.getContent()
        const prev = event.getPrevContent()

        const avatarChanged = content.avatar_url !== prev?.avatar_url
        const nameChanged = content.displayname !== prev?.displayname

        if (avatarChanged) {
          const roomItem = client.getRoom(roomId)
          const mxcUrl = roomItem?.getMxcAvatarUrl()
            ?? roomItem?.getAvatarFallbackMember()?.getMxcAvatarUrl()

          if (mxcUrl) {
            const httpUrl = client.mxcUrlToHttp(mxcUrl, 96, 96, 'crop', false, true, true)
            dispatch(fetchAvatarData({
              httpUrl,
              roomId
            }))
          }
        }

        if (nameChanged) {
          dispatch(setMatrixAccNameChange({
            roomId,
            name: content.displayname ?? client.getRoom(roomId)?.name
          }))
        }
      })

      const rooms = client.getRooms().map((room) => ({
        roomId: room.roomId,
        name: room.name
      }))

      return { rooms }
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// send a message
export const sendMessage = createAsyncThunk(
  'matrix/sendMessage',
  async ({ roomId, text, replyToEventId }, { rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      if (!client) throw new Error('Matrix client not started')

      const content = {
        msgtype: 'm.text',
        body: text
      }

      if (replyToEventId) {
        content['m.relates_to'] = {
          'm.in_reply_to': { event_id: replyToEventId }
        }
      }

      await client.sendMessage(roomId, content)
      return { roomId, text }
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// stop client
export const stopMatrixClient = createAsyncThunk(
  'matrix/stopClient',
  async (_, { dispatch }) => {
    dispatch(clearScheduledTokenRefresh())
    const client = getMatrixClient()
    if (client) client.stopClient()
    setMatrixClient(null)
    return true
  }
)

const getkeyId = async (client) => {
  try {
    const key = await client.http.authedRequest(
      'GET',
      `/user/${encodeURIComponent(client.getUserId())}/account_data/m.secret_storage.default_key`
    )

    return key?.key
  } catch (err) {
    console.error(err)
    return null
  }
}

export const checkIfHasRecoveryKey = createAsyncThunk(
  'matrix/checkIfHasRecoveryKey',
  async (_, { rejectWithValue }) => {
    try {
      const client = getMatrixClient()

      const existingKeyId = await getkeyId(client)

      if (existingKeyId) {
        return true
      }

      return false
    } catch (err) {
      console.error(err)
      return rejectWithValue(err.message)
    }
  }
)

export const checkIfNeedsRecoveryKeyInput = createAsyncThunk(
  'matrix/checkIfNeedsRecoveryKeyInput',
  async (_, { rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      const crypto = client.getCrypto()

      const existingKeyId = await getkeyId(client)

      if (!existingKeyId) return false

      const deviceStatus = await crypto.getDeviceVerificationStatus(
        client.getUserId(),
        client.getDeviceId()
      )
      const csStatus = await crypto.getCrossSigningStatus()
      const backupKey = await crypto.getSessionBackupPrivateKey()

      const isVerified = !!deviceStatus?.crossSigningVerified
      const hasCrossSigningKeys = !!csStatus.privateKeysCachedLocally?.masterKey

      return !isVerified || !hasCrossSigningKeys || !backupKey
    } catch (err) {
      console.error(err)
      return rejectWithValue(err.message)
    }
  }
)

export const changeRecoveryKey = createAsyncThunk(
  'matrix/changeRecoveryKey',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      const crypto = client.getCrypto()

      const status = await crypto.getCrossSigningStatus()
      if (!status.privateKeysCachedLocally?.masterKey) {
        await dispatch(
          getNotificationMessage({
            type: REJECTED,
            message: 'Verify this device first to change your recovery key',
            isEditProfile: false
          })
        )

        return rejectWithValue('SECRETS_NOT_AVAILABLE')
      }

      let recoveryKey
      await crypto.bootstrapSecretStorage({
        createSecretStorageKey: async () => {
          recoveryKey = await crypto.createRecoveryKeyFromPassphrase()
          return recoveryKey
        },
        setupNewSecretStorage: true
      })

      dispatch(
        getNotificationMessage({
          type: SUCCEEDED,
          message: 'Recovery key generated',
          isEditProfile: false
        })
      )

      return recoveryKey.encodedPrivateKey
    } catch (err) {
      console.error(err)
      return rejectWithValue(err.message)
    }
  }
)

export const setupRecoveryKey = createAsyncThunk(
  'matrix/setupRecoveryKey',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      const crypto = client.getCrypto()

      const existingKeyId = await getkeyId(client)

      if (existingKeyId) {
        await dispatch(
          getNotificationMessage({
            type: REJECTED,
            message: 'Already has a recovery key',
            isEditProfile: false
          })
        )

        return rejectWithValue('SECRET_STORAGE_EXISTS')
      }

      await crypto.bootstrapCrossSigning({
        authUploadDeviceSigningKeys: async (makeRequest) => makeRequest(null)
      })

      let recoveryKey
      await crypto.bootstrapSecretStorage({
        createSecretStorageKey: async () => {
          recoveryKey = await crypto.createRecoveryKeyFromPassphrase()

          return recoveryKey
        },
        setupNewSecretStorage: true,
        setupNewKeyBackup: true
      })

      dispatch(
        getNotificationMessage({
          type: SUCCEEDED,
          message: 'Recovery key generated',
          isEditProfile: false
        })
      )

      return recoveryKey.encodedPrivateKey
    } catch (err) {
      console.error(err)
      return rejectWithValue(err.message)
    }
  }
)

export const restoreWithRecoveryKey = createAsyncThunk(
  'matrix/restoreWithRecoveryKey',
  async ({ recoveryKey }, { dispatch, rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      const crypto = client.getCrypto()

      const keyId = await getkeyId(client)

      if (!keyId) {
        await dispatch(
          getNotificationMessage({
            type: REJECTED,
            message: 'No recovery key set up for this account',
            isEditProfile: false
          })
        )

        return rejectWithValue('NO_SECRET_STORAGE')
      }

      let privateKey
      try {
        privateKey = decodeRecoveryKey(recoveryKey.trim())
      } catch {
        await dispatch(
          getNotificationMessage({
            type: REJECTED,
            message: 'Invalid recovery key format',
            isEditProfile: false
          })
        )

        return rejectWithValue('INVALID_RECOVERY_KEY_FORMAT')
      }

      const [, keyInfo] = await client.secretStorage.getKey(keyId)
      const matches = await client.secretStorage.checkKey(privateKey, keyInfo)
      if (!matches) {
        await dispatch(
          getNotificationMessage({
            type: REJECTED,
            message: 'Invalid recovery key',
            isEditProfile: false
          })
        )

        return rejectWithValue('WRONG_RECOVERY_KEY')
      }

      secretStorageKeys.set(keyId, privateKey)

      await crypto.bootstrapCrossSigning({
        authUploadDeviceSigningKeys: async () => {
          throw new Error('CROSS_SIGNING_UPLOAD_NOT_EXPECTED')
        }
      })

      await crypto.loadSessionBackupPrivateKeyFromSecretStorage()
      await crypto.checkKeyBackupAndEnable()
      const result = await crypto.restoreKeyBackup()

      await dispatch(
        getNotificationMessage({
          type: SUCCEEDED,
          message: 'Verification successful',
          isEditProfile: false
        })
      )

      return {
        imported: result.imported,
        total: result.total
      }
    } catch (err) {
      console.error(err)
      return rejectWithValue(err.message)
    }
  }
)

export const resetRecoveryKey = createAsyncThunk(
  'matrix/resetRecoveryKey',
  async (body, { dispatch, rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      const crypto = client.getCrypto()

      await usersApi.allowCrossSigningReplacement({ password: body?.password })
      secretStorageKeys.clear()

      let recoveryKey
      try {
        await crypto.bootstrapSecretStorage({
          createSecretStorageKey: async () => {
            recoveryKey = await crypto.createRecoveryKeyFromPassphrase()
            return recoveryKey
          },
          setupNewSecretStorage: true
        })

        await crypto.bootstrapCrossSigning({
          setupNewCrossSigning: true,
          authUploadDeviceSigningKeys: async (makeRequest) => makeRequest(null)
        })
      } catch (err) {
        console.error(err)
        return rejectWithValue(err.message)
      }

      await crypto.resetKeyBackup()

      dispatch(
        getNotificationMessage({
          type: SUCCEEDED,
          message: 'Recovery key generated',
          isEditProfile: false
        })
      )

      return recoveryKey.encodedPrivateKey
    } catch (err) {
      console.error(err)

      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: err?.message ?? err?.data?.message,
          isEditProfile: false
        })
      )

      return rejectWithValue(err.message)
    }
  }
)
